import React, { FC } from "react";
import { makeStyles } from "@material-ui/styles";
import {
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  ListItemText,
  ListItemIcon,
} from "@material-ui/core";
import themeConfig from "../config";
import "./motifMultiAppSelect.scss";
import MotifAppCheckbox from "../../theme/motifAppCheckbox/motifAppCheckbox";
import AppIcon from "../appIcon/appIcon";
import { angleDown } from "../icons";
const useStyle = makeStyles({
  root: {
    width: "100%",
    fontFamily: themeConfig.eyInterstateLight,
    color: themeConfig.gray,
    textTransform: "capitalize",
  },
  select: {
    "&:focus": {
      backgroundColor: "transparent",
    },
  },
  menuPaper: {
    maxHeight: 260
  }
});

const ClickableIcon = ({onClick, style }) => (
  <div onClick={onClick} style={{right: style.right}} className="dropdown-icon">
    <AppIcon icon={angleDown}  />
  </div>
);

interface ISelectProps {
  id?: string;
  value?: string | string[];
  name?: string;
  isValid?: boolean;
  disabled?: boolean;
  label?: string;
  placeholder?: string;
  options: Array<{ value: string | number; label: string; isDisabled?:boolean }>;
  onChange?: (event: React.ChangeEvent<{ value: unknown }>) => void;
  className?: string;
  startAdornment?: any;
  endAdornment?: any;
  showWarning?: boolean;
  isRequired?: boolean;
  multiple?: boolean;
  displayEmpty?: boolean;
  emptyValue?: string;
  renderValue?: (selected: any) => React.ReactNode;
  MenuProps?: any;
  renderOption?: (option: any) => React.ReactNode;
  inputProps?: any;
  disableItems?:(selected: string[], option:Array<{ value: string | number; label: string; isDisabled?:boolean }>)=> boolean;
  menuProps?: any;
}

const MotifMultiAppSelect: FC<ISelectProps> = ({
  value,
  isValid,
  options,
  startAdornment,
  endAdornment,
  className,
  label,
  showWarning = false,
  isRequired,
  multiple = false,
  emptyValue = "",
  placeholder = "Select", // Default placeholder
  disableItems,
  menuProps = {},
  ...restProps
}) => {
  const classes = useStyle();
  const [open, setOpen] = React.useState(false);

  const startAdornmentElement = startAdornment ? (
    <InputAdornment position="start">{startAdornment}</InputAdornment>
  ) : null;

  const endAdornmentElement = endAdornment ? (
    <InputAdornment position="end">{endAdornment}</InputAdornment>
  ) : null;
  
  const handleOpen = () => {
    if (!restProps.disabled)
      setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const selectedValues: string[]=Array.isArray(value) ? value.map(String): value ? [String(value)] : [];
  const htmlTitle = multiple && value?.length ? options.filter(opt => (value as string[]).includes(opt.value as string)).map(opt => opt.label).join(", ")  : value as string
  return (
    <FormControl variant="outlined" classes={{ root: classes.root }} fullWidth>
      <div className="select-wrapper multi-app-select" title={htmlTitle}>
        <span className="input-label">{label}</span>
        {isRequired ? <span className="asterisk">*</span> : null}
        <Select
          className={`motif-select ${
            className || endAdornmentElement ? "shift-caret" : ""
          } ${isValid !== undefined && !isValid ? "in-valid" : ""} 
          ${showWarning ? "show-warning" : ""}`}
          displayEmpty
          emptyValue={emptyValue}
          open={open}
          onClose={handleClose}
          onOpen={handleOpen}
          multiple={multiple} // Enable multiple selections
          value={multiple ? selectedValues : selectedValues[0] || ""} // Ensure value is an array if multiple is true
          IconComponent={() => (
            <ClickableIcon onClick={handleOpen} style={{ right: endAdornment ? "30px" : "10px" }}/>
          )}
          renderValue={(selected) => {
            if (multiple) {
              return (selected as string[]).length === 0
                ? placeholder
                : options.filter(opt => (selected as string[]).includes(opt.value as string)).map(opt => opt.label).join(", ");
            }
            return <div>{selected || placeholder}</div>;
          }}
          {...restProps}
          MenuProps={{
            ...menuProps,
            anchorOrigin: {
              vertical: "bottom",
              horizontal: "left",
            },
            transformOrigin: {
              vertical: "top",
              horizontal: "left",
            },
            getContentAnchorEl: null,
          }}
        >
          {options.length < 1 ? (
            <MenuItem value={""} disabled>
              <ListItemText primary={emptyValue} />
            </MenuItem>
          ) : null}
          {options.map((option) => {
            const isDisabled = option.isDisabled || (disableItems ? disableItems(selectedValues, option): false);

            return(
              <MenuItem key={option.value} value={option.value} disabled={isDisabled}>
              {multiple && (
                  <ListItemIcon style={{ minWidth: 27 }}>
                    <MotifAppCheckbox checked={selectedValues.includes(String(option.value))} />
                  </ListItemIcon>
                )}
                <ListItemText className="item-text-style" primary={option.label} />
              </MenuItem>
            )
          })}
        </Select>
        {startAdornmentElement && (
          <span className="start-tooptip">{startAdornmentElement}</span>
        )}
        {endAdornmentElement && (
          <span className="endtooltip">{endAdornmentElement}</span>
        )}
      </div>
    </FormControl>
  );
};

export default MotifMultiAppSelect;
